import type { StatusBadgeVariant } from '../components/ui/StatusBadge';
import type { AsoebiAssignment, AssignmentPacking, ChecklistLine } from '../types/ui/AsoebiAssignment';
import { recordLabel } from './recordId';

export const PACKING_LABELS: Record<AssignmentPacking, string> = {
  not_started: 'Not started',
  partial: 'Part packed',
  complete: 'Packed',
};

export const PACKING_VARIANT: Record<AssignmentPacking, StatusBadgeVariant> = {
  not_started: 'neutral',
  partial: 'warning',
  complete: 'success',
};

export const PACKING_OPTIONS: Array<{ value: AssignmentPacking; label: string }> = [
  { value: 'not_started', label: PACKING_LABELS.not_started },
  { value: 'partial', label: PACKING_LABELS.partial },
  { value: 'complete', label: PACKING_LABELS.complete },
];

/**
 * Where an assignment stands. An assignment with a checklist is complete only
 * when every line is packed (the API says the same); one without a checklist
 * (a single fabric) has only the hand-set draft/complete.
 */
export function packingOf(a: Pick<AsoebiAssignment, 'status' | 'packed_count' | 'total_count'>): AssignmentPacking {
  if (a.status === 'complete') return 'complete';
  return a.packed_count > 0 ? 'partial' : 'not_started';
}

/** "2 of 5 lines" or, with no checklist, a dash. */
export function progressText(a: Pick<AsoebiAssignment, 'packed_count' | 'total_count'>): string {
  return a.total_count === 0 ? 'No checklist' : `${a.packed_count} of ${a.total_count} lines`;
}

/** What the header search matches: the references people read, the guest, the bundle and the notes. */
export function assignmentMatches(a: AsoebiAssignment, term: string): boolean {
  if (!term) return true;
  return [
    recordLabel('asoebiAssignment', a.id), recordLabel('fabricAssignment', a.order_id), a.id, a.order_id,
    a.guest_name, a.fabric_name, a.notes?.replace(/<[^>]*>/g, ' '),
  ].some(v => (v ?? '').toLowerCase().includes(term));
}

/** True when packing this line would take the item's stock below zero (the API allows it, as Kalligo does). */
export function wouldGoShort(line: Pick<ChecklistLine, 'quantity' | 'in_stock' | 'packed'>): boolean {
  if (line.packed || line.in_stock === null) return false;
  return Number(line.in_stock) < Number(line.quantity);
}

/** A readable quantity: "6", "2.5", never "6.000". */
export function fmtQuantity(q: string | null | undefined): string {
  if (q === null || q === undefined || q === '') return '';
  const n = Number(q);
  return Number.isFinite(n) ? String(n) : q;
}
